/**
 * Trình phát âm thanh dùng chung — mục 3.7 đặc tả giao diện.
 *
 * Viết MỘT LẦN ở đây, dùng lại ở BỐN nơi: nghe thử khi biên tập (MH-12), nghe
 * khi duyệt (MH-14), nghe trực tiếp luồng đang phát (MH-22/giám sát), và nghe
 * lại bản tin đã phát (MH-16).
 *
 * Hai chế độ:
 *  - tệp có thời lượng xác định (`url`): có thanh tua, tốc độ phát 1x/1,5x/2x,
 *    phím cách để phát/tạm dừng;
 *  - luồng trực tiếp (`cheDoLuong`): KHÔNG có thanh tua, hiển thị độ trễ luồng
 *    và nút đồng bộ lại về thời điểm hiện tại (đưa playback về cuối bộ đệm).
 *
 * Yêu cầu bắt buộc "nghe trước khi duyệt" của MH-14 đọc sự kiện `onNgheHet`
 * (phát tới cuối tệp ít nhất một lần), do nơi gọi truyền vào.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';

export type TocDoPhat = 1 | 1.5 | 2;
const TOC_DO_CHON: TocDoPhat[] = [1, 1.5, 2];

export interface TrinhPhatAmThanhProps {
  /** Nhãn cho trình đọc màn hình, ví dụ "Nghe thử bản tin sáng". */
  nhan: string;
  /** URL tệp có thời lượng xác định; bỏ trống khi ở chế độ luồng. */
  url?: string | null;
  /** Chế độ luồng trực tiếp: ẩn thanh tua, hiện độ trễ. */
  cheDoLuong?: boolean;
  /** Độ trễ luồng tính bằng giây, chỉ dùng khi `cheDoLuong`. */
  doTreGiay?: number;
  /** Gọi khi phát tới cuối tệp — dùng cho điều kiện "đã nghe" của MH-14. */
  onNgheHet?: () => void;
  /** Đồng bộ lại về thời điểm hiện tại, chỉ hiện ở chế độ luồng. */
  onDongBoLai?: () => void;
}

function thanhGio(giay: number): string {
  if (!Number.isFinite(giay) || giay < 0) return '0:00';
  const phut = Math.floor(giay / 60);
  const s = Math.floor(giay % 60);
  return `${phut}:${String(s).padStart(2, '0')}`;
}

export function TrinhPhatAmThanh({
  nhan,
  url,
  cheDoLuong = false,
  doTreGiay,
  onNgheHet,
  onDongBoLai,
}: TrinhPhatAmThanhProps): ReactNode {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [dangPhat, datDangPhat] = useState(false);
  const [thoiGianHienTai, datThoiGianHienTai] = useState(0);
  const [thoiLuong, datThoiLuong] = useState(0);
  const [tocDo, datTocDo] = useState<TocDoPhat>(1);
  const [tai, datTai] = useState<'dang-tai' | 'san-sang' | 'loi'>('dang-tai');
  const [daNgheHet, datDaNgheHet] = useState(false);
  const idThanhTua = useId();

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = tocDo;
  }, [tocDo]);

  // Phím cách phát/tạm dừng khi trình phát đang được thao tác (không chặn ô nhập khác).
  useEffect(() => {
    const nghe = (e: KeyboardEvent): void => {
      if (e.code !== 'Space') return;
      const dich = e.target as HTMLElement | null;
      if (dich && ['INPUT', 'TEXTAREA', 'SELECT'].includes(dich.tagName)) return;
      const vung = audioRef.current?.closest(`[data-vung-trinh-phat]`);
      if (!vung?.contains(document.activeElement) && document.activeElement !== vung) return;
      e.preventDefault();
      toggle();
    };
    document.addEventListener('keydown', nghe);
    return () => document.removeEventListener('keydown', nghe);
  }, [dangPhat]);

  const toggle = (): void => {
    const a = audioRef.current;
    if (!a) return;
    if (dangPhat) a.pause();
    else void a.play();
  };

  const veTien = useMemo(
    () => (thoiLuong > 0 ? (thoiGianHienTai / thoiLuong) * 100 : 0),
    [thoiGianHienTai, thoiLuong],
  );

  return (
    <div className="trinh-phat" data-vung-trinh-phat tabIndex={0} aria-label={nhan}>
      {url ? (
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          onCanPlay={() => datTai('san-sang')}
          onError={() => datTai('loi')}
          onPlay={() => datDangPhat(true)}
          onPause={() => datDangPhat(false)}
          onTimeUpdate={(e) => datThoiGianHienTai(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => datThoiLuong(e.currentTarget.duration || 0)}
          onEnded={() => {
            datDangPhat(false);
            datDaNgheHet(true);
            onNgheHet?.();
          }}
        />
      ) : null}

      <div className="trinh-phat__hang">
        <button
          type="button"
          className="nut nut--chinh trinh-phat__nut-phat"
          onClick={toggle}
          disabled={!url || tai === 'loi'}
          aria-pressed={dangPhat}
        >
          {dangPhat ? vi.trinhPhat.tamDung : vi.trinhPhat.phat}
        </button>

        {cheDoLuong ? (
          <>
            <span className="trinh-phat__luong" data-vung="che-do-luong">
              {vi.trinhPhat.cheDoLuong}
            </span>
            <span data-vung="do-tre">
              {vi.trinhPhat.doTre}: {doTreGiay ?? 0}s
            </span>
            {onDongBoLai ? (
              <button type="button" className="nut nut--nho" onClick={onDongBoLai}>
                {vi.trinhPhat.dongBoLai}
              </button>
            ) : null}
          </>
        ) : (
          <>
            <label htmlFor={idThanhTua} className="chi-cho-doc-man-hinh">
              {nhan}
            </label>
            <input
              id={idThanhTua}
              type="range"
              min={0}
              max={100}
              step={0.1}
              value={Number.isFinite(veTien) ? veTien : 0}
              onChange={(e) => {
                const a = audioRef.current;
                if (!a || thoiLuong <= 0) return;
                const ti = (Number(e.target.value) / 100) * thoiLuong;
                a.currentTime = ti;
                datThoiGianHienTai(ti);
              }}
              aria-label={vi.trinhPhat.phimTat}
            />
            <span data-vung="thoi-gian">
              {thanhGio(thoiGianHienTai)} / {thanhGio(thoiLuong)}
            </span>
            <label htmlFor={`${idThanhTua}-tocdo`} className="chi-cho-doc-man-hinh">
              {vi.trinhPhat.tocDo}
            </label>
            <select
              id={`${idThanhTua}-tocdo`}
              value={tocDo}
              onChange={(e) => datTocDo(Number(e.target.value) as TocDoPhat)}
              aria-label={vi.trinhPhat.tocDo}
            >
              {TOC_DO_CHON.map((t) => (
                <option key={t} value={t}>
                  {t}x
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {tai === 'dang-tai' && url ? <p className="trinh-phat__phu">{vi.trinhPhat.dangTai}</p> : null}
      {tai === 'loi' ? (
        <p className="trinh-phat__phu" role="alert">
          {vi.trinhPhat.loi}
        </p>
      ) : null}
      {daNgheHet ? (
        <p className="trinh-phat__phu" data-vung="da-nghe-het">
          {vi.trinhPhat.daNgheHet}
        </p>
      ) : null}
    </div>
  );
}
